import React, { useCallback, useContext } from 'react';
import { AreaBookingItem, Daybook, useDaybookQuery } from "../../generated/graphql";
import { useUserContext } from "../user-context";
import { differenceInDays, parseISO } from "date-fns";

const DaybookContext = React.createContext<Daybook | null>(null);

// we don't want to pass the entire campaign object around, so...
type CampaignBasic = {
    start_date?: string,
    created_date?: string
}


export type CalculateAreaBookingPricing<Type> = (campaignDates: CampaignBasic) => (row: Type) => number;

const AreaPricingContext = React.createContext<CalculateAreaBookingPricing<any> | null>(null);

export function useDaybookContext() {
    return useContext<Daybook | null>(DaybookContext);
}



export function useAreaPricingContext() {
    return useContext(AreaPricingContext);
}


export const DayBookContextProvider = ({ children }) => {
    const user = useUserContext();
    const { data } = useDaybookQuery({ skip: !user.isLoggedIn });
    const calculateAreaBookingPricing = useCallback((campaignDates: CampaignBasic) => (row: AreaBookingItem) => {
        if (!data?.daybook?.areaBookingPricingModels) {
            return 0;
        }
        if (!row.campaignComponent?.unit_value_prescribed_local) return 0;
        const price = row.campaignComponent.unit_value_prescribed_local;
        const fromDt = parseISO(row.date_up??'');
        const toDt = parseISO(row.date_down??'');
        if (!fromDt || !toDt) {
            return 0;
        }
        const weeks = Math.ceil(differenceInDays(toDt, fromDt) / 7);
        console.log('[DBC]', 'weeks', weeks, campaignDates, row);
        if (!weeks) return 0;
        let total: number | null = null;
        let currentMatch = -1;
        let model:any = null;
        data.daybook.areaBookingPricingModels.forEach((checkModel) => {
            if (total) return false;
            if (!checkModel) return false;
            let fieldValue: string | null = null;
            if (checkModel.code != row.campaignComponent?.region?.regionType?.pricing_model) return false;
            if (checkModel.priority as number < currentMatch) return false;
            if (checkModel.where) {
                const value = checkModel.where.value as string;
                if (checkModel.where?.campaignField) {
                    fieldValue = campaignDates[checkModel.where.campaignField] as string;
                    console.log('CCP compare', model, fieldValue, campaignDates);
                    // bit ugly
                    switch (checkModel.where.compare) {
                        case '<=':
                            if (fieldValue > value) return false;
                            break;
                        case '>':
                            if (fieldValue <= value) return false;
                            break;
                        default: break;
                    }
                }
                if (checkModel.where?.rowField) {
                    fieldValue = row[checkModel.where.rowField] as string;
                    switch (checkModel.where.compare) {
                        case '<=':
                            if (fieldValue > value) return;
                            break;
                        case '>':
                            if (fieldValue <= value) return;
                            break;
                        default: break;
                    }
                }
            }
            model = checkModel;
            currentMatch = checkModel.priority as number;
            console.log('CCP ok', model, fieldValue, campaignDates);
            return true;
        });
        if (!model) return 0;
        console.log('CCP model', model);
        total = price * weeks / (model.baseWeeks ?? 0);
        if (model.weekOverrides) {
            model.weekOverrides.forEach((override) => {
                if (override?.weeks != weeks) {
                    return;
                }
                total = price * (override.overrideWeeks ?? weeks) / (model.baseWeeks ?? 0);
                if (override.multiplier) {
                    total = total * override.multiplier;
                }
            });
        }

        console.log('[DBC0]', 'model', model);
        if (row.created_at) {
            if (row.campaignComponent?.region?.regionType?.has_split_qty) {
                console.log('[DBC]', 'regionCheck', row.qty_sites, model.locationsLessThanTrigger);
                if ((row.qty_sites ?? 0) < (model.locationsLessThanTrigger ?? 0)) {
                    console.log('[DBC]', 'small', total, model.locationsLessThanPercentage, model.locationsLessThanTrigger);
                    total = (total ?? 0) * ((100 + (model.locationsLessThanPercentage ?? 0)) / 100);
                }
            }
        }
        if (total === null) {
            throw ('unknown pricing model!!');
        }
        console.log('[DBC]', 'calced', total, weeks);
        return Math.floor(total);
    }, [data?.daybook?.areaBookingPricingModels]);

    return <DaybookContext.Provider value={data?.daybook ?? null}>
        <AreaPricingContext.Provider value={calculateAreaBookingPricing}>
            {children}
        </AreaPricingContext.Provider>
    </DaybookContext.Provider>;
};